import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, FileSpreadsheet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useHavData } from "./hav/hooks/useHavData";
import { HavTable, HavDecision, flattenHavRegistrations, HavTableRow } from "./hav/HavTable";
import { HavFilters, HavFiltersState } from "./hav/HavFilters";
import { exportToExcel } from "../utils/exportData";

const initialFilters: HavFiltersState = {
  state: "all",
  type: "all",
  name: "",
  sponsor: "",
  principal: "",
  paymentStatus: "",
};

export const HavTab = () => {
  const { registrations, isLoading, error, refetch } = useHavData();
  const [decisions, setDecisions] = useState<Record<string, HavDecision | undefined>>({});
  const [filters, setFilters] = useState<HavFiltersState>(initialFilters);
  const [isSending, setIsSending] = useState(false);
  const [showUnpaidConfirm, setShowUnpaidConfirm] = useState(false);

  const allRows = useMemo(() => flattenHavRegistrations(registrations), [registrations]);

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      if (filters.state !== "all") {
        const rowState =
          row.approval_status === "approved"
            ? "approved"
            : row.approval_status === "refused"
            ? "refused"
            : "pending";
        if (rowState !== filters.state) return false;
      }

      if (filters.type !== "all" && row.type !== filters.type) return false;

      const nameLower = filters.name.trim().toLowerCase();
      if (nameLower) {
        const fullName = `${row.first_name} ${row.last_name}`.toLowerCase();
        if (!fullName.includes(nameLower)) return false;
      }

      const sponsorLower = filters.sponsor.trim().toLowerCase();
      if (sponsorLower && !row.sponsor_name.toLowerCase().includes(sponsorLower)) return false;

      const principalLower = filters.principal.trim().toLowerCase();
      if (principalLower) {
        const principalName = (row.principal_name ?? "-").toLowerCase();
        if (!principalName.includes(principalLower)) return false;
      }

      const paymentLower = filters.paymentStatus.trim().toLowerCase();
      if (paymentLower && !row.payment_status.toLowerCase().includes(paymentLower)) return false;

      return true;
    });
  }, [allRows, filters]);

  const handleDecisionChange = (registrationId: string, decision: HavDecision) => {
    setDecisions((prev) => ({
      ...prev,
      [registrationId]: prev[registrationId] === decision ? undefined : decision,
    }));
  };

  const selectedIds = Object.keys(decisions).filter((id) => decisions[id]);

  const pendingEmails = selectedIds.reduce((acc, id) => {
    const reg = registrations.find((r) => r.id === id);
    const guestEmails = (reg?.guests ?? []).filter((g) => !!g.email).length;
    return acc + 1 + guestEmails;
  }, 0);

  const unpaidNames = useMemo(() => {
    const names: string[] = [];
    for (const id of selectedIds) {
      if (decisions[id] !== "approved") continue;
      const reg = registrations.find((r) => r.id === id);
      if (!reg || reg.payment_status === "paid") continue;
      names.push(`${reg.first_name} ${reg.last_name}`);
      for (const g of reg.guests ?? []) {
        names.push(`${g.first_name} ${g.last_name}`);
      }
    }
    return names;
  }, [selectedIds, decisions, registrations]);

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    const data = filteredRows.map((row: HavTableRow) => ({
      "NOM": row.last_name,
      "PRENOM": row.first_name,
      "IDENTIFIANT": row.ticket_code || "",
      "TYPE": row.type === "accompagnant" ? "Accompagnant" : "Principal",
      "ETAT": row.approval_status === "approved" ? "Validé" : row.approval_status === "refused" ? "Refusé" : "En attente",
      "Parrain": row.sponsor_name || "",
      "Participant principal": row.principal_name || "",
      "Montant (€)": row.amount ?? "",
      "Don suppl. (€)": row.additional_donation ?? "",
      "Paiement": row.payment_status,
      "Places": row.seats_count,
      "Catégorie": row.seat_category === "orchestre" ? "Orchestre" : row.seat_category === "balcon" ? "Balcon" : "",
      "Téléphone": row.phone || "",
      "Email": row.email,
    }));
    exportToExcel(data, `Soiree_AHV_${new Date().toISOString().split("T")[0]}`);
    toast({ title: "Export Excel réussi", description: `${filteredRows.length} ligne(s) exportée(s).` });
  };

  const handleSendClick = () => {
    if (selectedIds.length === 0) return;
    if (unpaidNames.length > 0) {
      setShowUnpaidConfirm(true);
      return;
    }
    void handleSend();
  };

  const handleSend = async () => {
    setShowUnpaidConfirm(false);
    if (selectedIds.length === 0) return;
    setIsSending(true);

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (sessionError || !accessToken) {
      setIsSending(false);
      toast({
        title: "Session expirée",
        description: "Veuillez vous reconnecter avant d’envoyer les emails.",
        variant: "destructive",
      });
      return;
    }

    let ok = 0;
    const failures: string[] = [];

    for (const id of selectedIds) {
      const decision = decisions[id];
      const fn = decision === "approved" ? "send-hav-confirmation" : "send-hav-refusal";
      const { data, error: fnError } = await supabase.functions.invoke(fn, {
        body: { registrationId: id },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const returned = (data as { error?: string })?.error;
      if (fnError || returned) {
        const reg = registrations.find((r) => r.id === id);
        let detail = returned ?? fnError?.message ?? "";
        const ctx = (fnError as { context?: Response })?.context;
        if (!returned && ctx && typeof ctx.text === "function") {
          try {
            detail = await ctx.text();
          } catch {
            /* ignore */
          }
        }
        console.error(`${fn} failed for ${id}:`, detail);
        failures.push(`${reg?.first_name ?? ""} ${reg?.last_name ?? id} (${detail})`);
      } else {
        ok += (data as { emailsSent?: number })?.emailsSent ?? 1;
      }
    }

    setIsSending(false);
    setDecisions({});
    await refetch();

    if (failures.length === 0) {
      toast({
        title: "Emails envoyés",
        description: `${ok} email(s) envoyé(s) avec succès.`,
      });
    } else {
      toast({
        title: "Envois partiels",
        description: `${ok} réussi(s), échec pour : ${failures.join(", ")}`,
        variant: "destructive",
      });
    }
  };

  const totalGuests = registrations.reduce((acc, r) => acc + (r.guests?.length ?? 0), 0);

  return (
    <>
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-base">
          Soirée « À Haute Voix » — {registrations.length} inscription(s), {registrations.length + totalGuests} participant(s)
          {filteredRows.length !== allRows.length && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({filteredRows.length} affiché(s))
            </span>
          )}
        </CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isLoading || filteredRows.length === 0}
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exporter ({filteredRows.length})
          </Button>
          <Button
            onClick={handleSendClick}
            disabled={isSending || selectedIds.length === 0}
            className="bg-[#00123A] hover:bg-[#00123A]/90"
          >
            <Send className="h-4 w-4 mr-2" />
            {isSending ? "Envoi en cours..." : `Envoyer les emails (${pendingEmails})`}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground">Chargement...</p>}
        {error && <p className="text-destructive">Erreur : {error.message}</p>}
        {!isLoading && !error && (
          <>
            <HavFilters filters={filters} onChange={setFilters} />
            <HavTable
              rows={filteredRows}
              decisions={decisions}
              onDecisionChange={handleDecisionChange}
            />
          </>
        )}
      </CardContent>
    </Card>

    <AlertDialog open={showUnpaidConfirm} onOpenChange={setShowUnpaidConfirm}>
      <AlertDialogContent className="bg-white z-[100]">
        <AlertDialogHeader>
          <AlertDialogTitle>Paiement non effectué !</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>Voulez-vous confirmer l'inscription ?</p>
              {unpaidNames.length > 1 && (
                <ul className="list-disc pl-5 text-sm">
                  {unpaidNames.map((n, i) => (
                    <li key={`${n}-${i}`}>{n}</li>
                  ))}
                </ul>
              )}
              {unpaidNames.length === 1 && <p className="text-sm">{unpaidNames[0]}</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => setShowUnpaidConfirm(false)}>
            Non
          </Button>
          <Button className="bg-[#00123A] hover:bg-[#00123A]/90" onClick={() => void handleSend()}>
            Oui
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
